import type { WorkerProgress } from '@saas-pages/shared/worker'

export const MAX_AUDIO_BYTES = 25 * 1024 * 1024
export const MAX_AUDIO_DURATION_SECONDS = 20 * 60
export const MAX_RESULT_BYTES = 25 * 1024 * 1024

export type AudioContainer = 'mp3' | 'wav' | 'webm'
export type AudioTrimFailureCode =
  | 'invalid_input'
  | 'unsupported_input'
  | 'size_limit'
  | 'unsupported_output'
  | 'processing_failed'

export interface AudioMetadata {
  container: AudioContainer
  codec: string
  durationSeconds: number
  mimeType: string
  extension: string
  channels: number
  sampleRate: number
}

export type AudioTrimWorkerInput =
  | { action: 'inspect'; file: Blob }
  | { action: 'trim'; file: Blob; startSeconds: number; endSeconds: number }

export type AudioTrimWorkerResult =
  | { action: 'inspect'; metadata: AudioMetadata }
  | { action: 'trim'; metadata: AudioMetadata; result: Blob; snappedStartSeconds: number; snappedEndSeconds: number }

export class AudioTrimError extends Error {
  readonly code: AudioTrimFailureCode

  constructor(code: AudioTrimFailureCode) {
    super(code)
    this.name = 'AudioTrimError'
    this.code = code
  }
}

export function validateSource(file: Blob): void {
  if (!(file instanceof Blob) || !Number.isSafeInteger(file.size) || file.size <= 0) {
    throw new AudioTrimError('invalid_input')
  }
  if (file.size > MAX_AUDIO_BYTES) throw new AudioTrimError('size_limit')
}

export function validateRange(startSeconds: number, endSeconds: number, durationSeconds: number): void {
  if (
    !Number.isFinite(startSeconds)
    || !Number.isFinite(endSeconds)
    || !Number.isFinite(durationSeconds)
    || durationSeconds <= 0
    || startSeconds < 0
    || endSeconds <= startSeconds
    || endSeconds > durationSeconds + 0.001
  ) throw new AudioTrimError('invalid_input')
}

export function isAudioTrimWorkerResult(value: unknown): value is AudioTrimWorkerResult {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<AudioTrimWorkerResult>
  if (candidate.action !== 'inspect' && candidate.action !== 'trim') return false
  if (!isAudioMetadata(candidate.metadata)) return false
  if (candidate.action === 'inspect') return true
  const trim = candidate as Partial<Extract<AudioTrimWorkerResult, { action: 'trim' }>>
  return trim.result instanceof Blob
    && trim.result.size > 0
    && trim.result.size <= MAX_RESULT_BYTES
    && typeof trim.snappedStartSeconds === 'number'
    && Number.isFinite(trim.snappedStartSeconds)
    && typeof trim.snappedEndSeconds === 'number'
    && Number.isFinite(trim.snappedEndSeconds)
    && trim.snappedEndSeconds > trim.snappedStartSeconds
}

export function isAudioMetadata(value: unknown): value is AudioMetadata {
  if (typeof value !== 'object' || value === null) return false
  const metadata = value as Partial<AudioMetadata>
  return (metadata.container === 'mp3' || metadata.container === 'wav' || metadata.container === 'webm')
    && typeof metadata.codec === 'string'
    && metadata.codec.length > 0
    && typeof metadata.durationSeconds === 'number'
    && Number.isFinite(metadata.durationSeconds)
    && metadata.durationSeconds > 0
    && metadata.durationSeconds <= MAX_AUDIO_DURATION_SECONDS
    && typeof metadata.mimeType === 'string'
    && metadata.mimeType.startsWith('audio/')
    && typeof metadata.extension === 'string'
    && metadata.extension.length > 0
    && typeof metadata.channels === 'number'
    && Number.isSafeInteger(metadata.channels)
    && metadata.channels > 0
    && typeof metadata.sampleRate === 'number'
    && Number.isSafeInteger(metadata.sampleRate)
    && metadata.sampleRate > 0
}

export async function inspectAudio(file: Blob): Promise<AudioMetadata> {
  validateSource(file)
  const { inspectAudioWithEngine } = await import('@/features/audio-trimmer/model/mediaEngine')
  return inspectAudioWithEngine(file)
}

export async function trimAudio(
  file: Blob,
  startSeconds: number,
  endSeconds: number,
  onProgress: (progress: WorkerProgress) => void,
): Promise<Extract<AudioTrimWorkerResult, { action: 'trim' }>> {
  validateSource(file)
  const { trimAudioWithEngine } = await import('@/features/audio-trimmer/model/mediaEngine')
  return trimAudioWithEngine(file, startSeconds, endSeconds, onProgress)
}